import {
  PermissionFieldPath,
  PermissionOptionsInterface,
  PermissionsItem,
  PermissionsSection,
  PermissionsSectionGroup,
} from "@/pages/settings/roles/edit/role-edit.types.ts";
import { useEmployeeMe } from "@/queries/employees/use-employees.query.ts";
import {
  ApiEmployeeLifecycleStatus,
  EmployeeGenders,
  LifecycleStatusTexts,
} from "@/types/employees/employee.types.ts";
import {
  formatDateCondition,
  ApiRoleCustomTableSavePayload,
  EmployeeFieldPermission,
  EmployeeFieldPermissions,
  PermissionType,
  Rule,
  RuleApplySignType,
  RuleConditionsItems,
  RuleConditionType,
  RuleSignItems,
} from "@/types/role.types.ts";
import { ApiFeatureType } from "@/types/subscription.types.ts";
import { ApiFieldTypes, ApiTable, ApiTableTypes } from "@/types/custom-field.types.ts";
import { useTranslation } from "react-i18next";

export const getRolePermissionTables = (tables: ApiTable[] = []): ApiTable[] =>
  tables.flatMap((table) => {
    const customFields = table.table_fields.filter((field) => field.type === ApiFieldTypes.custom);

    if (table.table_type === ApiTableTypes.custom) {
      return [{ ...table, table_fields: customFields }];
    }

    return customFields.length > 0 ? [{ ...table, table_fields: customFields }] : [];
  });

export const systemTableManagePermissionFields: Partial<
  Record<ApiTableTypes, EmployeeFieldPermission>
> = {
  [ApiTableTypes.employee_positions]: EmployeeFieldPermission.manage_employee_position,
  [ApiTableTypes.employee_employments]: EmployeeFieldPermission.manage_employee_employment,
  [ApiTableTypes.employee_terminations]: EmployeeFieldPermission.manage_employee_termination,
  [ApiTableTypes.employee_compensations]: EmployeeFieldPermission.manage_employee_compensation,
  [ApiTableTypes.employee_additional_compensations]:
    EmployeeFieldPermission.employee_additional_compensation,
};

export const getSystemTableFieldPermission = (
  tableType: ApiTableTypes,
  managePermission: PermissionType | undefined,
  fieldPermission: PermissionType
): PermissionType =>
  tableType !== ApiTableTypes.custom && managePermission === PermissionType.edit
    ? PermissionType.edit
    : fieldPermission;

export const getSystemTableBasePermissions = (
  tableType: ApiTableTypes,
  permissions: EmployeeFieldPermissions
): Pick<
  ApiRoleCustomTableSavePayload,
  "history" | "manage_table" | "effective_from" | "changed_by"
> | null => {
  if (tableType === ApiTableTypes.employee_additional_compensations) {
    const permission = permissions[EmployeeFieldPermission.employee_additional_compensation];

    return {
      manage_table: permission === PermissionType.edit ? PermissionType.edit : PermissionType.none,
      history: permission,
      effective_from: permission,
      changed_by: permission,
    };
  }

  const permissionFields: Partial<
    Record<
      ApiTableTypes,
      {
        manage: EmployeeFieldPermission;
        history: EmployeeFieldPermission;
        effectiveFrom: EmployeeFieldPermission;
        changedBy: EmployeeFieldPermission;
      }
    >
  > = {
    [ApiTableTypes.employee_positions]: {
      manage: EmployeeFieldPermission.manage_employee_position,
      history: EmployeeFieldPermission.employee_positions_history,
      effectiveFrom: EmployeeFieldPermission.employee_position_date,
      changedBy: EmployeeFieldPermission.employee_position_changed_by,
    },
    [ApiTableTypes.employee_employments]: {
      manage: EmployeeFieldPermission.manage_employee_employment,
      history: EmployeeFieldPermission.employee_employments_history,
      effectiveFrom: EmployeeFieldPermission.employee_employment_date,
      changedBy: EmployeeFieldPermission.employee_employment_changed_by,
    },
    [ApiTableTypes.employee_terminations]: {
      manage: EmployeeFieldPermission.manage_employee_termination,
      history: EmployeeFieldPermission.employee_employments_history,
      effectiveFrom: EmployeeFieldPermission.employee_termination_date,
      changedBy: EmployeeFieldPermission.employee_termination_changed_by,
    },
    [ApiTableTypes.employee_compensations]: {
      manage: EmployeeFieldPermission.manage_employee_compensation,
      history: EmployeeFieldPermission.employee_compensations_history,
      effectiveFrom: EmployeeFieldPermission.employee_compensation_date,
      changedBy: EmployeeFieldPermission.employee_compensation_changed_by,
    },
  };
  const fields = permissionFields[tableType];

  if (!fields) return null;

  return {
    manage_table: permissions[fields.manage],
    history: permissions[fields.history],
    effective_from: permissions[fields.effectiveFrom],
    changed_by: permissions[fields.changedBy],
  };
};

export interface FieldPermissionConfigItemDescription extends Pick<PermissionType, never> {
  [key: string]: string;
}

export interface PermissionConfigItemBase {
  title: string;
  availableOptions: PermissionType[];
  field: PermissionFieldPath;
  availableOptionsDescription?: FieldPermissionConfigItemDescription;
  customPermissionTypesTexts?: Partial<Record<PermissionType, string>>;
}

export default function useRoleEditService() {
  const { t } = useTranslation();
  const { data: employeeMe } = useEmployeeMe();

  const getEmployeeFieldPermissionSections = () => {
    return [
      {
        title: t("core:personal"),
        groups: [
          {
            title: t("common:general"),
            items: [
              {
                title: t("core:lifecycle_status"),
                availableOptions: [PermissionType.none, PermissionType.view],
                field: "employee_field_permissions.employee_lifecycle_status",
                renderAs: PermissionOptionsInterface.select,
              },
              {
                title: t("common:last_seen"),
                availableOptions: [PermissionType.none, PermissionType.view],
                field: "employee_field_permissions.employee_last_activity_date",
                renderAs: PermissionOptionsInterface.select,
              },
              {
                title: t("core:portal_access"),
                availableOptions: [PermissionType.none, PermissionType.view, PermissionType.edit],
                field: "employee_field_permissions.employee_portal_access",
                renderAs: PermissionOptionsInterface.select,
                availableOptionsDescription: {
                  [PermissionType.view]: t("core:portal_access_permission_view_description"),
                  [PermissionType.edit]: t("core:portal_access_permission_edit_description"),
                },
              },
              {
                title: t("time_off:time_off"),
                availableOptions: [PermissionType.none, PermissionType.view, PermissionType.edit],
                field: "employee_field_permissions.employee_time_off",
                renderAs: PermissionOptionsInterface.select,
                customPermissionTypesTexts: {
                  [PermissionType.edit]: t("common:permission_type_view_and_manage_requests"),
                },
              },
              ...(employeeMe?.plan.features[ApiFeatureType.clocking]?.available
                ? [
                    {
                      title: t("clocking:permissions_entries_title"),
                      availableOptions: [
                        PermissionType.none,
                        PermissionType.view,
                        PermissionType.edit,
                      ],
                      field: "employee_field_permissions.clocking_entries",
                      renderAs: PermissionOptionsInterface.select,
                    },
                  ]
                : []),
              ...(employeeMe?.plan.features[ApiFeatureType.goals].available
                ? [
                    {
                      title: t("goals:personal_goals"),
                      availableOptions: [
                        PermissionType.none,
                        PermissionType.view,
                        PermissionType.edit,
                      ],
                      field: "employee_field_permissions.employee_goals",
                      renderAs: PermissionOptionsInterface.select,
                    },
                  ]
                : []),
              ...(employeeMe?.plan.features[ApiFeatureType.recognitions].available
                ? [
                    {
                      title: t("recognition:profile_title"),
                      availableOptions: [
                        PermissionType.none,
                        PermissionType.view,
                        PermissionType.edit,
                      ],
                      field: "employee_field_permissions.employee_recognitions",
                      renderAs: PermissionOptionsInterface.select,
                      customPermissionTypesTexts: {
                        [PermissionType.none]: t("common:permission_type_none"),
                        [PermissionType.view]: t("recognition:permission_type_view"),
                        [PermissionType.edit]: t("recognition:permission_type_view_and_gift"),
                      },
                    },
                  ]
                : []),
            ],
          },
          {
            title: t("core:personal"),
            items: [
              {
                title: t("core:full_name"),
                availableOptions: [PermissionType.view, PermissionType.edit],
                field: "employee_field_permissions.employee_name",
                renderAs: PermissionOptionsInterface.select,
              },
              {
                title: t("core:name_display_level"),
                availableOptions: [PermissionType.edit, PermissionType.view, PermissionType.none],
                field: "employee_field_permissions.employee_name_display",
                renderAs: PermissionOptionsInterface.select,
                customPermissionTypesTexts: {
                  [PermissionType.edit]: t("core:name_display_level_full"),
                  [PermissionType.view]: t("core:name_display_level_initial"),
                  [PermissionType.none]: t("core:name_display_level_first_only"),
                },
                availableOptionsDescription: {
                  [PermissionType.edit]: t("core:name_display_level_full_description"),
                  [PermissionType.view]: t("core:name_display_level_initial_description"),
                  [PermissionType.none]: t("core:name_display_level_first_only_description"),
                },
              },
              {
                title: t("common:name_pronunciation"),
                availableOptions: [PermissionType.none, PermissionType.view, PermissionType.edit],
                field: "employee_field_permissions.employee_name_pronunciation",
                renderAs: PermissionOptionsInterface.select,
              },
              {
                title: t("core:employee_number"),
                availableOptions: [PermissionType.none, PermissionType.view, PermissionType.edit],
                field: "employee_field_permissions.employee_number",
                renderAs: PermissionOptionsInterface.select,
              },
              {
                title: t("core:hired_on"),
                availableOptions: [PermissionType.view, PermissionType.edit],
                field: "employee_field_permissions.employee_hired_on",
                renderAs: PermissionOptionsInterface.select,
              },
              {
                title: t("core:email"),
                availableOptions: [PermissionType.none, PermissionType.view, PermissionType.edit],
                field: "employee_field_permissions.employee_email",
                renderAs: PermissionOptionsInterface.select,
              },
              {
                title: t("core:email_personal"),
                availableOptions: [PermissionType.none, PermissionType.view, PermissionType.edit],
                field: "employee_field_permissions.employee_email_personal",
                renderAs: PermissionOptionsInterface.select,
              },
              {
                title: t("core:phone"),
                availableOptions: [PermissionType.none, PermissionType.view, PermissionType.edit],
                field: "employee_field_permissions.employee_phone",
                renderAs: PermissionOptionsInterface.select,
              },
              {
                title: t("core:phone_personal"),
                availableOptions: [PermissionType.none, PermissionType.view, PermissionType.edit],
                field: "employee_field_permissions.employee_phone_personal",
                renderAs: PermissionOptionsInterface.select,
              },
              {
                title: t("integrations:slack_title"),
                availableOptions: [PermissionType.none, PermissionType.view],
                field: "employee_field_permissions.employee_slack",
                renderAs: PermissionOptionsInterface.select,
              },
              {
                title: t("core:gender"),
                availableOptions: [PermissionType.none, PermissionType.view, PermissionType.edit],
                field: "employee_field_permissions.employee_gender",
                renderAs: PermissionOptionsInterface.select,
              },
              {
                title: t("core:birth_date"),
                availableOptions: [PermissionType.none, PermissionType.view, PermissionType.edit],
                field: "employee_field_permissions.employee_date_birth",
                renderAs: PermissionOptionsInterface.select,
                customPermissionTypesTexts: {
                  [PermissionType.none]: t("core:visibility_managed_by_organization"),
                  [PermissionType.view]: t("core:birth_date_visibility_view"),
                  [PermissionType.edit]: t("core:birth_date_visibility_edit"),
                },
              },
              {
                title: t("core:birthday_display_settings"),
                availableOptions: [PermissionType.none, PermissionType.view, PermissionType.edit],
                field: "employee_field_permissions.employee_date_birth_display",
                renderAs: PermissionOptionsInterface.select,
                customPermissionTypesTexts: {
                  [PermissionType.none]: t("core:birthday_display_settings_permission_none"),
                  [PermissionType.view]: t("core:birthday_display_settings_permission_view"),
                  [PermissionType.edit]: t("core:birthday_display_settings_permission_edit"),
                },
                availableOptionsDescription: {
                  [PermissionType.none]: t(
                    "core:birthday_display_settings_permission_none_description"
                  ),
                  [PermissionType.view]: t(
                    "core:birthday_display_settings_permission_view_description"
                  ),
                  [PermissionType.edit]: t(
                    "core:birthday_display_settings_permission_edit_description"
                  ),
                },
              },
              {
                title: t("common:photo"),
                availableOptions: [PermissionType.view, PermissionType.edit],
                field: "employee_field_permissions.employee_avatar",
                renderAs: PermissionOptionsInterface.select,
              },
              {
                title: t("common:background"),
                availableOptions: [PermissionType.view, PermissionType.edit],
                field: "employee_field_permissions.employee_background",
                renderAs: PermissionOptionsInterface.select,
              },

              {
                title: t("common:timezone"),
                availableOptions: [PermissionType.none, PermissionType.view, PermissionType.edit],
                field: "employee_field_permissions.employee_timezone",
                renderAs: PermissionOptionsInterface.select,
              },
            ] as PermissionsItem[],
          },
        ],
      },
      {
        title: t("core:address"),
        groups: [
          {
            title: t("core:address"),
            items: [
              {
                title: t("core:address_line_1"),
                availableOptions: [PermissionType.none, PermissionType.view, PermissionType.edit],
                field: "employee_field_permissions.employee_address_address_line_1",
                renderAs: PermissionOptionsInterface.select,
              },
              {
                title: t("core:address_line_2"),
                availableOptions: [PermissionType.none, PermissionType.view, PermissionType.edit],
                field: "employee_field_permissions.employee_address_address_line_2",
                renderAs: PermissionOptionsInterface.select,
              },
              {
                title: t("core:city"),
                availableOptions: [PermissionType.none, PermissionType.view, PermissionType.edit],
                field: "employee_field_permissions.employee_address_city",
                renderAs: PermissionOptionsInterface.select,
              },
              {
                title: t("core:state"),
                availableOptions: [PermissionType.none, PermissionType.view, PermissionType.edit],
                field: "employee_field_permissions.employee_address_state",
                renderAs: PermissionOptionsInterface.select,
              },
              {
                title: t("core:zip"),
                availableOptions: [PermissionType.none, PermissionType.view, PermissionType.edit],
                field: "employee_field_permissions.employee_address_zip_code",
                renderAs: PermissionOptionsInterface.select,
              },
              {
                title: t("core:country"),
                availableOptions: [PermissionType.none, PermissionType.view, PermissionType.edit],
                field: "employee_field_permissions.employee_address_country",
                renderAs: PermissionOptionsInterface.select,
              },
            ] as PermissionsItem[],
          },
        ],
      },
      {
        title: t("core:position"),
        groups: [
          {
            title: t("core:position"),
            items: [
              {
                title: t("core:manage_position"),
                description: t("core:manage_position_description"),
                availableOptions: [PermissionType.none, PermissionType.edit],
                field: "employee_field_permissions.manage_employee_position",
                renderAs: PermissionOptionsInterface.switch,
              },
            ] as PermissionsItem[],
          },
          {
            title: t("common:view"),
            hiddenWhenField: "employee_field_permissions.manage_employee_position",
            hiddenWhenValue: PermissionType.edit,
            items: [
              {
                title: t("common:history"),
                availableOptions: [PermissionType.none, PermissionType.view],
                field: "employee_field_permissions.employee_positions_history",
                renderAs: PermissionOptionsInterface.select,
              },
              {
                title: t("core:changed_by"),
                availableOptions: [PermissionType.none, PermissionType.view],
                field: "employee_field_permissions.employee_position_changed_by",
                renderAs: PermissionOptionsInterface.select,
              },
              {
                title: t("core:position"),
                availableOptions: [PermissionType.none, PermissionType.view],
                field: "employee_field_permissions.employee_position_job_title",
                renderAs: PermissionOptionsInterface.select,
              },
              {
                title: t("core:job_track"),
                availableOptions: [PermissionType.none, PermissionType.view],
                field: "employee_field_permissions.employee_position_job_track",
                renderAs: PermissionOptionsInterface.select,
              },
              {
                title: t("core:job_grade"),
                availableOptions: [PermissionType.none, PermissionType.view],
                field: "employee_field_permissions.employee_position_job_grade",
                renderAs: PermissionOptionsInterface.select,
              },
              {
                title: t("core:job_salary_range"),
                availableOptions: [PermissionType.none, PermissionType.view],
                field: "employee_field_permissions.employee_position_job_salary",
                renderAs: PermissionOptionsInterface.select,
              },
              {
                title: t("common:date_effective_from"),
                availableOptions: [PermissionType.none, PermissionType.view],
                field: "employee_field_permissions.employee_position_date",
                renderAs: PermissionOptionsInterface.select,
              },
              {
                title: t("core:manager"),
                availableOptions: [PermissionType.none, PermissionType.view],
                field: "employee_field_permissions.employee_position_reporting_to",
                renderAs: PermissionOptionsInterface.select,
              },
              {
                title: t("core:division"),
                availableOptions: [PermissionType.none, PermissionType.view],
                field: "employee_field_permissions.employee_position_division",
                renderAs: PermissionOptionsInterface.select,
              },
              {
                title: t("core:department"),
                availableOptions: [PermissionType.none, PermissionType.view],
                field: "employee_field_permissions.employee_position_department",
                renderAs: PermissionOptionsInterface.select,
              },
              {
                title: t("common:location"),
                availableOptions: [PermissionType.none, PermissionType.view],
                field: "employee_field_permissions.employee_position_location",
                renderAs: PermissionOptionsInterface.select,
              },
              {
                title: t("common:comments"),
                availableOptions: [PermissionType.none, PermissionType.view],
                field: "employee_field_permissions.employee_position_comment",
                renderAs: PermissionOptionsInterface.select,
              },
            ] as PermissionsItem[],
          },
        ],
      },
      {
        title: t("core:employment"),
        groups: [
          {
            title: t("core:employment"),
            items: [
              {
                title: t("core:manage_employment"),
                description: t("core:manage_employment_description"),
                availableOptions: [PermissionType.none, PermissionType.edit],
                field: "employee_field_permissions.manage_employee_employment",
                renderAs: PermissionOptionsInterface.switch,
              },
            ] as PermissionsItem[],
          },
          {
            title: t("common:view"),
            hiddenWhenField: "employee_field_permissions.manage_employee_employment",
            hiddenWhenValue: PermissionType.edit,
            items: [
              {
                title: t("common:history"),
                availableOptions: [PermissionType.none, PermissionType.view],
                field: "employee_field_permissions.employee_employments_history",
                renderAs: PermissionOptionsInterface.select,
              },
              {
                title: t("core:changed_by"),
                availableOptions: [PermissionType.none, PermissionType.view],
                field: "employee_field_permissions.employee_employment_changed_by",
                renderAs: PermissionOptionsInterface.select,
              },
              {
                title: t("common:date_effective_from"),
                availableOptions: [PermissionType.none, PermissionType.view],
                field: "employee_field_permissions.employee_employment_date",
                renderAs: PermissionOptionsInterface.select,
              },
              {
                title: t("core:legal_entity"),
                availableOptions: [PermissionType.none, PermissionType.view],
                field: "employee_field_permissions.employee_employment_legal_entity",
                renderAs: PermissionOptionsInterface.select,
              },
              {
                title: t("core:employment_type"),
                availableOptions: [PermissionType.none, PermissionType.view],
                field: "employee_field_permissions.employee_employment_employment_type",
                renderAs: PermissionOptionsInterface.select,
              },
              {
                title: t("core:employment_contract"),
                availableOptions: [PermissionType.none, PermissionType.view],
                field: "employee_field_permissions.employee_employment_contract",
                renderAs: PermissionOptionsInterface.select,
              },
              {
                title: t("core:work_pattern"),
                availableOptions: [PermissionType.none, PermissionType.view],
                field: "employee_field_permissions.employee_employment_work_pattern",
                renderAs: PermissionOptionsInterface.select,
              },
              {
                title: t("core:probation"),
                availableOptions: [PermissionType.none, PermissionType.view],
                field: "employee_field_permissions.employee_employment_probation",
                renderAs: PermissionOptionsInterface.select,
              },
              {
                title: t("common:comments"),
                availableOptions: [PermissionType.none, PermissionType.view],
                field: "employee_field_permissions.employee_employment_comment",
                renderAs: PermissionOptionsInterface.select,
              },
            ] as PermissionsItem[],
          },
        ],
      },
      {
        title: t("core:termination"),
        groups: [
          {
            title: t("core:termination"),
            items: [
              {
                title: t("core:terminate_employee"),
                description: t("core:permissions_terminate_employees_description"),
                field: "employee_field_permissions.manage_employee_termination",
                availableOptions: [PermissionType.none, PermissionType.edit],
                renderAs: PermissionOptionsInterface.switch,
              },
            ] as PermissionsItem[],
          },
          {
            title: t("common:view"),
            hiddenWhenField: "employee_field_permissions.manage_employee_termination",
            hiddenWhenValue: PermissionType.edit,
            items: [
              {
                title: t("core:termination_date"),
                availableOptions: [PermissionType.none, PermissionType.view],
                field: "employee_field_permissions.employee_termination_date",
                renderAs: PermissionOptionsInterface.select,
              },
              {
                title: t("core:last_working_day"),
                availableOptions: [PermissionType.none, PermissionType.view],
                field: "employee_field_permissions.employee_termination_last_working_date",
                renderAs: PermissionOptionsInterface.select,
              },
              {
                title: t("core:leave_type"),
                availableOptions: [PermissionType.none, PermissionType.view],
                field: "employee_field_permissions.employee_termination_leave_type",
                renderAs: PermissionOptionsInterface.select,
              },
              {
                title: t("core:termination_reason"),
                availableOptions: [PermissionType.none, PermissionType.view],
                field: "employee_field_permissions.employee_termination_reason",
                renderAs: PermissionOptionsInterface.select,
              },
              {
                title: t("core:termination_handover_to"),
                availableOptions: [PermissionType.none, PermissionType.view],
                field: "employee_field_permissions.employee_termination_replacement_employee",
                renderAs: PermissionOptionsInterface.select,
              },
              {
                title: t("core:eligible_for_rehire"),
                availableOptions: [PermissionType.none, PermissionType.view],
                field: "employee_field_permissions.employee_termination_eligible_for_rehire",
                renderAs: PermissionOptionsInterface.select,
              },
              {
                title: t("common:comments"),
                availableOptions: [PermissionType.none, PermissionType.view],
                field: "employee_field_permissions.employee_termination_comment",
                renderAs: PermissionOptionsInterface.select,
              },
              {
                title: t("core:changed_by"),
                availableOptions: [PermissionType.none, PermissionType.view],
                field: "employee_field_permissions.employee_termination_changed_by",
                renderAs: PermissionOptionsInterface.select,
              },
            ] as PermissionsItem[],
          },
        ],
      },
      {
        title: t("core:compensation"),
        groups: [
          {
            title: t("core:compensation"),
            items: [
              {
                title: t("core:manage_compensation"),
                description: t("core:manage_compensation_description"),
                availableOptions: [PermissionType.none, PermissionType.edit],
                field: "employee_field_permissions.manage_employee_compensation",
                renderAs: PermissionOptionsInterface.switch,
              },
            ] as PermissionsItem[],
          },
          {
            title: t("common:view"),
            hiddenWhenField: "employee_field_permissions.manage_employee_compensation",
            hiddenWhenValue: PermissionType.edit,
            items: [
              {
                title: t("common:history"),
                availableOptions: [PermissionType.none, PermissionType.view],
                field: "employee_field_permissions.employee_compensations_history",
                renderAs: PermissionOptionsInterface.select,
              },
              {
                title: t("core:changed_by"),
                availableOptions: [PermissionType.none, PermissionType.view],
                field: "employee_field_permissions.employee_compensation_changed_by",
                renderAs: PermissionOptionsInterface.select,
              },
              {
                title: t("common:date_effective_from"),
                availableOptions: [PermissionType.none, PermissionType.view],
                field: "employee_field_permissions.employee_compensation_date",
                renderAs: PermissionOptionsInterface.select,
              },
              {
                title: t("core:compensation_change_reason"),
                availableOptions: [PermissionType.none, PermissionType.view],
                field: "employee_field_permissions.employee_compensation_change_reason",
                renderAs: PermissionOptionsInterface.select,
              },
              {
                title: t("core:base_salary_gross"),
                availableOptions: [PermissionType.none, PermissionType.view],
                field: "employee_field_permissions.employee_compensation_base_salary_gross",
                renderAs: PermissionOptionsInterface.select,
              },
              {
                title: t("core:base_salary_net"),
                availableOptions: [PermissionType.none, PermissionType.view],
                field: "employee_field_permissions.employee_compensation_base_salary_net",
                renderAs: PermissionOptionsInterface.select,
              },
              {
                title: t("core:permission_employee_compensation_lines_title"),
                description: t("core:permission_employee_compensation_lines_description"),
                availableOptions: [PermissionType.none, PermissionType.view],
                field: "employee_field_permissions.employee_compensation_lines",
                renderAs: PermissionOptionsInterface.select,
              },
              {
                title: t("core:permission_employee_compensation_total_cost_title"),
                description: t("core:permission_employee_compensation_total_cost_description"),
                availableOptions: [PermissionType.none, PermissionType.view],
                field: "employee_field_permissions.employee_compensation_total_cost",
                renderAs: PermissionOptionsInterface.select,
              },
              {
                title: t("common:comments"),
                availableOptions: [PermissionType.none, PermissionType.view],
                field: "employee_field_permissions.employee_compensation_comment",
                renderAs: PermissionOptionsInterface.select,
              },
            ] as PermissionsItem[],
          },
          {
            title: t("core:additional_compensation"),
            items: [
              {
                title: t("core:additional_compensation"),
                availableOptions: [PermissionType.none, PermissionType.view, PermissionType.edit],
                field: "employee_field_permissions.employee_additional_compensation",
                renderAs: PermissionOptionsInterface.select,
              },
            ] as PermissionsItem[],
          },
        ],
      },
    ] as PermissionsSection[];
  };

  const getFeaturesPermissionsSections = () => {
    if (!employeeMe) return [];

    let newsSection: PermissionsSection | undefined = undefined;

    if (employeeMe.plan.features[ApiFeatureType.blog].available) {
      newsSection = {
        title: t("blog:news"),
        groups: [
          {
            title: t("blog:news"),
            items: [
              {
                title: t("blog:manage_channels"),
                description: t("blog:manage_channels_description"),
                field: "api_permissions.news_manage_channels",
                availableOptions: [PermissionType.none, PermissionType.edit],
                renderAs: PermissionOptionsInterface.switch,
              },
              {
                title: t("blog:manage_posts"),
                description: t("blog:manage_posts_description"),
                field: "api_permissions.news_manage_posts",
                availableOptions: [PermissionType.none, PermissionType.edit],
                renderAs: PermissionOptionsInterface.switch,
              },
              {
                title: t("blog:manage_comments"),
                description: t("blog:manage_comments_description"),
                field: "api_permissions.news_delete_all_comments",
                availableOptions: [PermissionType.none, PermissionType.edit],
                renderAs: PermissionOptionsInterface.switch,
              },
            ],
          },
        ],
      };
    }

    const isTasksAvailable = employeeMe.plan.features[ApiFeatureType.tasks].available;

    let surveysSection: PermissionsSection | undefined = undefined;
    let performanceReviewSection: PermissionsSection | undefined = undefined;
    let goalsSection: PermissionsSection | undefined = undefined;
    const isSurveysAvailable = employeeMe.plan.features[ApiFeatureType.talent_surveys].available;
    const isGoalsAvailable = employeeMe.plan.features[ApiFeatureType.goals].available;
    const isPerformanceReviewsAvailable =
      employeeMe.plan.features[ApiFeatureType.talent_performance_reviews].available;

    if (isSurveysAvailable) {
      surveysSection = {
        title: t("surveys:surveys"),
        groups: [
          {
            title: t("surveys:surveys"),
            items: [
              {
                title: t("surveys:surveys_module"),
                description: t("surveys:surveys_module_description"),
                field: "api_permissions.surveys_module",
                availableOptions: [PermissionType.none, PermissionType.edit],
                renderAs: PermissionOptionsInterface.switch,
                children: [
                  {
                    title: t("surveys:permissions_surveys_create"),
                    description: t("surveys:permissions_surveys_create_description"),
                    field: "api_permissions.surveys_create",
                    availableOptions: [PermissionType.none, PermissionType.edit],
                    renderAs: PermissionOptionsInterface.switch,
                  },
                  {
                    title: t("surveys:permissions_surveys_manage_all"),
                    description: t("surveys:permissions_surveys_manage_all_description"),
                    field: "api_permissions.surveys_manage_all",
                    availableOptions: [PermissionType.none, PermissionType.edit],
                    renderAs: PermissionOptionsInterface.switch,
                  },
                ],
              },
            ],
          },
          {
            title: t("common:templates"),
            items: [
              {
                title: t("surveys:permissions_survey_templates_title"),
                field: "api_permissions.survey_templates",
                availableOptions: [PermissionType.none, PermissionType.view, PermissionType.edit],
                availableOptionsDescription: {
                  [PermissionType.none]: t("surveys:permissions_survey_templates_none_description"),
                  [PermissionType.view]: t("surveys:permissions_survey_templates_view_description"),
                  [PermissionType.edit]: t("surveys:permissions_survey_templates_edit_description"),
                },
                renderAs: PermissionOptionsInterface.select,
              },
              {
                title: t("surveys:permissions_form_scales_title"),
                field: "api_permissions.form_scales",
                availableOptions: [PermissionType.view, PermissionType.edit],
                availableOptionsDescription: {
                  [PermissionType.none]: "-",
                  [PermissionType.view]: t("surveys:permissions_form_scales_view_description"),
                  [PermissionType.edit]: t("surveys:permissions_form_scales_edit_description"),
                },
                renderAs: PermissionOptionsInterface.select,
              },
            ],
          },
        ],
      };
    }

    if (isPerformanceReviewsAvailable) {
      performanceReviewSection = {
        title: t("performance_review:performance_review"),
        groups: [
          {
            title: t("performance_review:performance_review"),
            items: [
              {
                title: t("performance_review:performance_reviews_module"),
                description: t("performance_review:performance_reviews_module_description"),
                field: "api_permissions.performance_reviews_module",
                availableOptions: [PermissionType.none, PermissionType.edit],
                renderAs: PermissionOptionsInterface.switch,
                children: [
                  {
                    title: t("performance_review:permissions_performance_reviews_create"),
                    description: t(
                      "performance_review:permissions_performance_reviews_create_description"
                    ),
                    field: "api_permissions.performance_reviews_create",
                    availableOptions: [PermissionType.none, PermissionType.edit],
                    renderAs: PermissionOptionsInterface.switch,
                  },
                  {
                    title: t("performance_review:permissions_performance_reviews_manage_all"),
                    description: t(
                      "performance_review:permissions_performance_reviews_manage_all_description"
                    ),
                    field: "api_permissions.performance_reviews_manage_all",
                    availableOptions: [PermissionType.none, PermissionType.edit],
                    renderAs: PermissionOptionsInterface.switch,
                  },
                ],
              },
            ],
          },
          {
            title: t("common:templates"),
            items: [
              {
                title: t("performance_review:permissions_performance_review_templates_title"),
                field: "api_permissions.performance_review_templates",
                availableOptions: [PermissionType.none, PermissionType.view, PermissionType.edit],
                availableOptionsDescription: {
                  [PermissionType.none]: t(
                    "performance_review:permissions_performance_review_templates_none_description"
                  ),
                  [PermissionType.view]: t(
                    "performance_review:permissions_performance_review_templates_view_description"
                  ),
                  [PermissionType.edit]: t(
                    "performance_review:permissions_performance_review_templates_edit_description"
                  ),
                },
                renderAs: PermissionOptionsInterface.select,
              },
            ],
          },
        ],
      };
    }

    if (isGoalsAvailable) {
      goalsSection = {
        title: t("goals:goals"),
        groups: [
          {
            title: t("goals:goals"),
            items: [
              {
                title: t("goals:permissions_module_title"),
                description: t("goals:permissions_module_description"),
                field: "api_permissions.goals_module",
                availableOptions: [PermissionType.none, PermissionType.edit],
                renderAs: PermissionOptionsInterface.switch,
                children: [
                  {
                    title: t("goals:strategic_goals"),
                    description: t("goals:permissions_company_description"),
                    field: "api_permissions.goals_company_manage",
                    availableOptions: [
                      PermissionType.none,
                      PermissionType.view,
                      PermissionType.edit,
                    ],
                    renderAs: PermissionOptionsInterface.select,
                  },
                  {
                    title: t("goals:permissions_department_all_title"),
                    description: t("goals:permissions_department_all_description"),
                    field: "api_permissions.goals_department_manage_all",
                    availableOptions: [
                      PermissionType.none,
                      PermissionType.view,
                      PermissionType.edit,
                    ],
                    renderAs: PermissionOptionsInterface.select,
                  },
                  {
                    title: t("goals:permissions_department_own_title"),
                    description: t("goals:permissions_department_own_description"),
                    field: "api_permissions.goals_department_manage_own",
                    availableOptions: [
                      PermissionType.none,
                      PermissionType.view,
                      PermissionType.edit,
                    ],
                    renderAs: PermissionOptionsInterface.select,
                  },
                  {
                    title: t("goals:permissions_team_all_title"),
                    description: t("goals:permissions_team_all_description"),
                    field: "api_permissions.goals_team_manage_all",
                    availableOptions: [
                      PermissionType.none,
                      PermissionType.view,
                      PermissionType.edit,
                    ],
                    renderAs: PermissionOptionsInterface.select,
                  },
                  {
                    title: t("goals:permissions_team_own_title"),
                    description: t("goals:permissions_team_own_description"),
                    field: "api_permissions.goals_team_manage_own",
                    availableOptions: [
                      PermissionType.none,
                      PermissionType.view,
                      PermissionType.edit,
                    ],
                    renderAs: PermissionOptionsInterface.select,
                  },
                ],
              },
            ],
          },
        ],
      };
    }

    const generalSection: PermissionsSection = {
      title: t("common:general"),
      groups: [
        {
          title: t("common:general"),
          items: [
            {
              title: t("core:permissions_see_employee_directory"),
              description: t("core:permissions_see_employee_directory_description"),
              field: "api_permissions.see_employee_directory",
              availableOptions: [PermissionType.none, PermissionType.edit],
              renderAs: PermissionOptionsInterface.switch,
              children: [
                {
                  title: t("core:permissions_employees_search"),
                  description: t("core:permissions_employees_search_description"),
                  field: "api_permissions.employees_search",
                  availableOptions: [PermissionType.none, PermissionType.edit],
                  renderAs: PermissionOptionsInterface.switch,
                },
                {
                  title: t("core:permissions_add_new_employees"),
                  description: t("core:permissions_add_new_employees_description"),
                  field: "api_permissions.add_employees",
                  availableOptions: [PermissionType.none, PermissionType.edit],
                  renderAs: PermissionOptionsInterface.switch,
                },
                {
                  title: t("core:permissions_view_org_chart"),
                  description: t("core:permissions_view_org_chart_description"),
                  field: "employee_field_permissions.see_organization_structure",
                  availableOptions: [PermissionType.none, PermissionType.edit],
                  renderAs: PermissionOptionsInterface.switch,
                  dataAccessDependent: true,
                },
                {
                  title: t("core:permissions_view_departments_chart"),
                  description: t("core:permissions_view_departments_chart_description"),
                  field: "employee_field_permissions.see_departments_structure",
                  availableOptions: [PermissionType.none, PermissionType.edit],
                  renderAs: PermissionOptionsInterface.switch,
                  dataAccessDependent: true,
                },
                {
                  title: t("core:permissions_ban_employees"),
                  description: t("core:permissions_ban_employees_description"),
                  field: "api_permissions.ban_employees",
                  availableOptions: [PermissionType.none, PermissionType.edit],
                  renderAs: PermissionOptionsInterface.switch,
                },
                {
                  title: t("core:permissions_delete_employees"),
                  description: t("core:permissions_delete_employees_description"),
                  field: "api_permissions.delete_employees",
                  availableOptions: [PermissionType.none, PermissionType.edit],
                  renderAs: PermissionOptionsInterface.switch,
                },
                {
                  title: t("core:permissions_impersonate_employees"),
                  description: t("core:permissions_impersonate_employees_description"),
                  field: "api_permissions.impersonate_employees",
                  availableOptions: [PermissionType.none, PermissionType.edit],
                  renderAs: PermissionOptionsInterface.switch,
                },
              ],
            },
          ],
        },
        {
          title: t("core:core_hr"),
          items: [
            {
              title: t("core:permissions_access_to_leave_timeline"),
              description: t("core:permissions_access_to_leave_timeline_description"),
              field: "employee_field_permissions.access_to_leave_timeline",
              availableOptions: [PermissionType.none, PermissionType.edit],
              renderAs: PermissionOptionsInterface.switch,
              dataAccessDependent: true,
            },
            {
              title: t("core:permissions_approve_and_reject_leaves"),
              description: t("core:permissions_approve_and_reject_leaves_description"),
              field: "api_permissions.approve_and_reject_leaves",
              availableOptions: [PermissionType.none, PermissionType.edit],
              renderAs: PermissionOptionsInterface.switch,
            },
            ...(isTasksAvailable
              ? ([
                  {
                    title: t("tasks:permissions_module_title"),
                    description: t("tasks:permissions_module_description"),
                    field: "api_permissions.tasks_module",
                    availableOptions: [PermissionType.none, PermissionType.edit],
                    renderAs: PermissionOptionsInterface.switch,
                    children: [
                      {
                        title: t("tasks:permissions_create_title"),
                        description: t("tasks:permissions_create_description"),
                        field: "api_permissions.tasks_create",
                        availableOptions: [PermissionType.none, PermissionType.edit],
                        renderAs: PermissionOptionsInterface.switch,
                      },
                      {
                        title: t("tasks:permissions_manage_all_title"),
                        description: t("tasks:permissions_manage_all_description"),
                        field: "api_permissions.tasks_manage_all",
                        availableOptions: [PermissionType.none, PermissionType.edit],
                        renderAs: PermissionOptionsInterface.switch,
                      },
                    ],
                  },
                ] as PermissionsItem[])
              : []),
            ...(employeeMe.plan.features[ApiFeatureType.clocking]?.available
              ? ([
                  {
                    title: t("clocking:permissions_module_title"),
                    description: t("clocking:permissions_module_description"),
                    field: "api_permissions.clocking_module",
                    availableOptions: [PermissionType.none, PermissionType.edit],
                    renderAs: PermissionOptionsInterface.switch,
                  },
                ] as PermissionsItem[])
              : []),
          ],
        },
        {
          title: t("core:teams"),
          items: [
            {
              title: t("core:manage_teams"),
              description: "",
              field: "api_permissions.manage_teams",
              availableOptions: [PermissionType.none, PermissionType.view, PermissionType.edit],
              availableOptionsDescription: {
                [PermissionType.none]: t("core:manage_teams_settings_permission_none_description"),
                [PermissionType.view]: t("core:manage_teams_settings_permission_view_description"),
                [PermissionType.edit]: t("core:manage_teams_settings_permission_edit_description"),
              },
              renderAs: PermissionOptionsInterface.select,
            },
          ],
        },
      ],
    };

    const recruitSection: PermissionsSection = {
      title: t("ats:recruit"),
      groups: [
        {
          title: t("ats:recruit"),
          items: [
            {
              title: t("ats:recruit_module"),
              description: t("ats:recruit_module_description"),
              field: "api_permissions.recruiting_module",
              availableOptions: [PermissionType.none, PermissionType.edit],
              renderAs: PermissionOptionsInterface.switch,
            },
          ],
        },
        {
          title: t("ats:jobs"),
          hiddenWhenField: "api_permissions.recruiting_module",
          hiddenWhenValue: PermissionType.none,
          items: [
            {
              title: t("ats:permissions_show_all_jobs"),
              description: t("ats:permissions_show_all_jobs_description"),
              field: "api_permissions.show_all_jobs",
              availableOptions: [PermissionType.none, PermissionType.edit],
              renderAs: PermissionOptionsInterface.switch,
            },
            {
              title: t("ats:permissions_manage_jobs"),
              description: t("ats:permissions_manage_jobs_description"),
              field: "api_permissions.manage_jobs",
              availableOptions: [PermissionType.none, PermissionType.edit],
              renderAs: PermissionOptionsInterface.switch,
            },
            {
              title: t("ats:permissions_show_salary"),
              description: t("ats:permissions_show_salary_description"),
              field: "api_permissions.show_jobs_salaries",
              availableOptions: [PermissionType.none, PermissionType.edit],
              renderAs: PermissionOptionsInterface.switch,
            },
            {
              title: t("ats:permissions_show_offers_salary"),
              description: t("ats:permissions_show_offers_salary_description"),
              field: "api_permissions.show_offer_salary",
              availableOptions: [PermissionType.none, PermissionType.edit],
              renderAs: PermissionOptionsInterface.switch,
            },
            {
              title: t("ats:permissions_manage_offers"),
              field: "api_permissions.manage_offers",
              availableOptions: [PermissionType.none, PermissionType.view, PermissionType.edit],
              renderAs: PermissionOptionsInterface.select,
            },
          ],
        },
        {
          title: t("ats:candidates"),
          hiddenWhenField: "api_permissions.recruiting_module",
          hiddenWhenValue: PermissionType.none,
          items: [
            {
              title: t("ats:permissions_show_all_candidates"),
              description: t("ats:permissions_show_all_candidates_description"),
              field: "api_permissions.show_all_candidates",
              availableOptions: [PermissionType.none, PermissionType.edit],
              renderAs: PermissionOptionsInterface.switch,
            },
            {
              title: t("ats:permissions_manage_candidates"),
              description: t("ats:permissions_manage_candidates_description"),
              field: "api_permissions.manage_candidates",
              availableOptions: [PermissionType.none, PermissionType.edit],
              renderAs: PermissionOptionsInterface.switch,
            },
            {
              title: t("ats:permissions_delete_candidates"),
              description: t("ats:permissions_delete_candidates_description"),
              field: "api_permissions.delete_candidates",
              availableOptions: [PermissionType.none, PermissionType.edit],
              renderAs: PermissionOptionsInterface.switch,
            },
            {
              title: t("ats:permissions_show_candidate_salary"),
              description: t("ats:permissions_show_candidate_salary_description"),
              field: "api_permissions.show_candidates_salaries",
              availableOptions: [PermissionType.none, PermissionType.edit],
              renderAs: PermissionOptionsInterface.switch,
            },
            {
              title: t("ats:permissions_see_original_resume"),
              description: t("ats:permissions_see_original_resume_description"),
              field: "api_permissions.see_original_resume",
              availableOptions: [PermissionType.none, PermissionType.edit],
              renderAs: PermissionOptionsInterface.switch,
            },
            {
              title: t("ats:permissions_delete_all_comments"),
              description: t("ats:permissions_delete_all_comments_description"),
              field: "api_permissions.delete_all_comments",
              availableOptions: [PermissionType.none, PermissionType.edit],
              renderAs: PermissionOptionsInterface.switch,
            },
          ],
        },
        {
          title: t("common:reporting"),
          hiddenWhenField: "api_permissions.recruiting_module",
          hiddenWhenValue: PermissionType.none,
          items: [
            {
              title: t("ats:report_hiring_pipeline_title"),
              description: t("ats:permissions_reporting_hiring_pipeline_description"),
              field: "api_permissions.reporting_recruiting_stage_flow",
              availableOptions: [PermissionType.none, PermissionType.edit],
              renderAs: PermissionOptionsInterface.switch,
            },
            {
              title: t("ats:report_source_efficiency_title"),
              description: t("ats:permissions_reporting_recruiting_source_efficiency_description"),
              field: "api_permissions.reporting_recruiting_source_efficiency",
              availableOptions: [PermissionType.none, PermissionType.edit],
              renderAs: PermissionOptionsInterface.switch,
            },
            {
              title: t("ats:report_closing_time_title"),
              description: t("ats:permissions_reporting_recruiting_closing_time_description"),
              field: "api_permissions.reporting_recruiting_closing_time",
              availableOptions: [PermissionType.none, PermissionType.edit],
              renderAs: PermissionOptionsInterface.switch,
            },
            {
              title: t("ats:report_disqualification_reasons_title"),
              description: t(
                "ats:permissions_reporting_recruiting_disqualification_reasons_description"
              ),
              field: "api_permissions.reporting_recruiting_disqualification_reasons",
              availableOptions: [PermissionType.none, PermissionType.edit],
              renderAs: PermissionOptionsInterface.switch,
            },
          ],
        },
      ],
    };

    let hiringPlanSectionGroup: PermissionsSectionGroup | undefined = undefined;

    if (employeeMe.plan.features[ApiFeatureType.ats_hiring_plan].available) {
      hiringPlanSectionGroup = {
        title: t("hiring_plan:hiring_plan"),
        hiddenWhenField: "api_permissions.recruiting_module",
        hiddenWhenValue: PermissionType.none,
        items: [
          {
            title: t("hiring_plan:manage_title"),
            field: "api_permissions.manage_hiring_plan",
            availableOptions: [PermissionType.none, PermissionType.view, PermissionType.edit],
            renderAs: PermissionOptionsInterface.select,
          },
        ],
      };
    }

    if (hiringPlanSectionGroup) recruitSection.groups.push(hiringPlanSectionGroup);

    let scorecardsSectionGroup: PermissionsSectionGroup | undefined = undefined;

    if (employeeMe.plan.features[ApiFeatureType.ats_scorecards].available) {
      scorecardsSectionGroup = {
        title: t("scorecards:scorecards"),
        hiddenWhenField: "api_permissions.recruiting_module",
        hiddenWhenValue: PermissionType.none,
        items: [
          {
            title: t("scorecards:manage_scorecards"),
            field: "api_permissions.ats_manage_scorecards",
            availableOptions: [PermissionType.none, PermissionType.edit],
            renderAs: PermissionOptionsInterface.switch,
          },
        ],
      };
    }

    if (scorecardsSectionGroup) recruitSection.groups.push(scorecardsSectionGroup);

    const reportingSection: PermissionsSection = {
      title: t("common:reporting"),
      groups: [
        {
          title: t("common:general"),
          items: [
            {
              title: t("core:permission_reporting_export_title"),
              description: t("core:permissions_reporting_export_description"),
              field: "api_permissions.reporting_export",
              availableOptions: [PermissionType.none, PermissionType.edit],
              renderAs: PermissionOptionsInterface.switch,
            },
          ],
        },
        {
          title: t("time_off:time_off"),
          items: [
            {
              title: t("common:balances"),
              description: t("reporting:permissions_time_off_balance_report"),
              field: "api_permissions.reporting_leave_balance",
              availableOptions: [PermissionType.none, PermissionType.edit],
              renderAs: PermissionOptionsInterface.switch,
            },
            {
              title: t("reporting:time_off_history_report_title"),
              description: t("reporting:permissions_time_off_history_report"),
              field: "api_permissions.reporting_leave_used",
              availableOptions: [PermissionType.none, PermissionType.edit],
              renderAs: PermissionOptionsInterface.switch,
            },
          ],
        },
        {
          title: t("core:employees"),
          items: [
            {
              title: t("reporting:headcount_report_title"),
              description: t("reporting:permissions_headcount_report_description"),
              field: "api_permissions.reporting_employee_headcount",
              availableOptions: [PermissionType.none, PermissionType.edit],
              renderAs: PermissionOptionsInterface.switch,
            },
            {
              title: t("reporting:salary_report_title"),
              description: t("reporting:permissions_salary_report_description"),
              field: "api_permissions.reporting_employee_salary",
              availableOptions: [PermissionType.none, PermissionType.edit],
              renderAs: PermissionOptionsInterface.switch,
              children: [
                {
                  title: t("core:permission_reporting_employee_total_cost_title"),
                  description: t("core:permission_reporting_employee_total_cost_description"),
                  field: "api_permissions.reporting_employee_total_cost",
                  availableOptions: [PermissionType.none, PermissionType.edit],
                  renderAs: PermissionOptionsInterface.switch,
                  dependency: {
                    fields: ["api_permissions.reporting_employee_salary"],
                    getState: (getPermission) => {
                      const salaryReportingDisabled =
                        getPermission("api_permissions.reporting_employee_salary") ===
                        PermissionType.none;

                      return {
                        disabled: salaryReportingDisabled,
                        value: salaryReportingDisabled ? PermissionType.none : undefined,
                      };
                    },
                  },
                },
              ],
            },
            {
              title: t("reporting:retention_report_title"),
              description: t("reporting:permissions_retention_report_description"),
              field: "api_permissions.reporting_employee_retention",
              availableOptions: [PermissionType.none, PermissionType.edit],
              renderAs: PermissionOptionsInterface.switch,
            },
            {
              title: t("reporting:people_analytics_title"),
              description: t("reporting:permissions_people_analytics_report_description"),
              field: "api_permissions.reporting_people_analytics",
              availableOptions: [PermissionType.none, PermissionType.edit],
              renderAs: PermissionOptionsInterface.switch,
            },
          ],
        },
      ],
    };

    const settingsSection: PermissionsSection = {
      title: t("common:settings"),
      groups: [
        {
          title: t("common:general"),
          items: [
            {
              title: t("core:organization"),
              description: t("core:permissions_manage_organization_description"),
              field: "api_permissions.settings_organization",
              availableOptions: [PermissionType.none, PermissionType.edit],
              renderAs: PermissionOptionsInterface.switch,
            },
            {
              title: t("integrations:settings_integrations_title"),
              description: t("core:permissions_manage_integrations_description"),
              field: "api_permissions.settings_integrations",
              availableOptions: [PermissionType.none, PermissionType.edit],
              renderAs: PermissionOptionsInterface.switch,
            },
            {
              title: t("core:divisions"),
              description: t("core:permissions_manage_divisions_description"),
              field: "api_permissions.settings_divisions",
              availableOptions: [PermissionType.none, PermissionType.edit],
              renderAs: PermissionOptionsInterface.switch,
            },
            {
              title: t("core:departments"),
              description: t("core:permissions_manage_departments_description"),
              field: "api_permissions.settings_departments",
              availableOptions: [PermissionType.none, PermissionType.edit],
              renderAs: PermissionOptionsInterface.switch,
            },
            {
              title: t("core:positions_and_levels"),
              description: t("core:permissions_manage_positions_description"),
              field: "api_permissions.settings_positions_levels",
              availableOptions: [PermissionType.none, PermissionType.edit],
              renderAs: PermissionOptionsInterface.switch,
            },
            {
              title: t("common:locations"),
              description: t("core:permissions_manage_locations_description"),
              field: "api_permissions.settings_locations",
              availableOptions: [PermissionType.none, PermissionType.edit],
              renderAs: PermissionOptionsInterface.switch,
            },
            {
              title: t("core:document_folders"),
              description: t("core:permissions_manage_documents_description"),
              field: "api_permissions.settings_document_folders",
              availableOptions: [PermissionType.none, PermissionType.edit],
              renderAs: PermissionOptionsInterface.switch,
            },
            {
              title: t("core:legal_entities"),
              description: t("core:permissions_manage_legal_entities"),
              field: "api_permissions.settings_legal_entities",
              availableOptions: [PermissionType.none, PermissionType.edit],
              renderAs: PermissionOptionsInterface.switch,
            },
            {
              title: t("core:employment_types"),
              description: t("core:settings_employment_types_description"),
              field: "api_permissions.settings_employment_types",
              availableOptions: [PermissionType.none, PermissionType.edit],
              renderAs: PermissionOptionsInterface.switch,
            },
            {
              title: t("core:employment_contracts"),
              description: t("core:settings_employment_contracts_description"),
              field: "api_permissions.settings_employment_contracts",
              availableOptions: [PermissionType.none, PermissionType.edit],
              renderAs: PermissionOptionsInterface.switch,
            },
            {
              title: t("core:compensation_change_reasons"),
              description: t("core:permissions_manage_compensation_change_reasons_description"),
              field: "api_permissions.settings_compensation_change_reasons",
              availableOptions: [PermissionType.none, PermissionType.edit],
              renderAs: PermissionOptionsInterface.switch,
            },

            {
              title: t("core:compensation_types"),
              description: t("core:permissions_manage_compensation_types_description"),
              field: "api_permissions.settings_compensation_types",
              availableOptions: [PermissionType.none, PermissionType.edit],
              renderAs: PermissionOptionsInterface.switch,
            },
            {
              title: t("core:probation_policies"),
              description: t("core:permissions_manage_probation_policies_description"),
              field: "api_permissions.settings_probation_policies",
              availableOptions: [PermissionType.none, PermissionType.edit],
              renderAs: PermissionOptionsInterface.switch,
            },
            {
              title: t("core:work_patterns"),
              description: t("core:permissions_manage_work_patterns_description"),
              field: "api_permissions.settings_work_patterns",
              availableOptions: [PermissionType.none, PermissionType.edit],
              renderAs: PermissionOptionsInterface.switch,
            },
            {
              title: t("core:organization_links"),
              description: t("core:permissions_manage_organization_links_description"),
              field: "api_permissions.settings_organization_links",
              availableOptions: [PermissionType.none, PermissionType.edit],
              renderAs: PermissionOptionsInterface.switch,
            },
            ...(employeeMe?.plan.features[ApiFeatureType.recognitions].available
              ? ([
                  {
                    title: t("core:recognitions"),
                    description: t("core:permissions_manage_recognitions_description"),
                    field: "api_permissions.settings_recognitions",
                    availableOptions: [PermissionType.none, PermissionType.edit],
                    renderAs: PermissionOptionsInterface.switch,
                    children: [
                      {
                        title: t("core:recognitions_mass_gift"),
                        description: t("core:permissions_recognitions_mass_gift_description"),
                        field: "api_permissions.recognitions_mass_gift",
                        availableOptions: [PermissionType.none, PermissionType.edit],
                        renderAs: PermissionOptionsInterface.switch,
                      },
                    ],
                  },
                ] as PermissionsItem[])
              : []),
          ],
        },
        {
          title: t("core:core_hr"),
          items: [
            {
              title: t("core:permissions_manage_holidays"),
              description: t("core:permissions_manage_holidays_description"),
              field: "api_permissions.manage_holidays",
              availableOptions: [PermissionType.none, PermissionType.edit],
              renderAs: PermissionOptionsInterface.switch,
            },
            {
              title: t("core:permissions_manage_leave_policies"),
              description: t("core:permissions_manage_leave_policies_description"),
              field: "api_permissions.manage_leave_policies",
              availableOptions: [PermissionType.none, PermissionType.edit],
              renderAs: PermissionOptionsInterface.switch,
            },
            {
              title: t("core:permissions_manage_approval_policy"),
              description: t("core:permissions_manage_approval_policy_description"),
              field: "api_permissions.manage_approval_policy",
              availableOptions: [PermissionType.none, PermissionType.edit],
              renderAs: PermissionOptionsInterface.switch,
            },

            {
              title: t("custom_fields:custom_fields"),
              description: t("core:permissions_manage_custom_fields_description"),
              field: "api_permissions.manage_custom_fields",
              availableOptions: [PermissionType.none, PermissionType.edit],
              renderAs: PermissionOptionsInterface.switch,
            },
            {
              title: t("core:permissions_manage_hiring_flows"),
              description: t("core:permissions_manage_hiring_flows_description"),
              field: "api_permissions.settings_hiring_flows",
              availableOptions: [PermissionType.none, PermissionType.edit],
              renderAs: PermissionOptionsInterface.switch,
            },
          ],
        },
        {
          title: t("ats:recruit"),
          items: [
            {
              title: t("ats:pipelines"),
              description: t("ats:permissions_manage_pipelines_description"),
              field: "api_permissions.settings_pipelines",
              availableOptions: [PermissionType.none, PermissionType.edit],
              renderAs: PermissionOptionsInterface.switch,
            },
            {
              title: t("ats:sources"),
              description: t("ats:permissions_manage_sources_description"),
              field: "api_permissions.settings_sources",
              availableOptions: [PermissionType.none, PermissionType.edit],
              renderAs: PermissionOptionsInterface.switch,
            },
            {
              title: t("ats:tags"),
              description: t("ats:permissions_manage_tags_description"),
              field: "api_permissions.manage_candidates",
              availableOptions: [PermissionType.none, PermissionType.edit],
              renderAs: PermissionOptionsInterface.switch,
            },
            {
              title: t("ats:disqualification_reasons"),
              description: t("ats:permissions_manage_disqualification_reasons_description"),
              field: "api_permissions.settings_candidate_disqualification_reasons",
              availableOptions: [PermissionType.none, PermissionType.edit],
              renderAs: PermissionOptionsInterface.switch,
            },
            {
              title: t("core:email_templates"),
              description: t("core:permissions_manage_email_templates_description"),
              field: "api_permissions.settings_templates",
              availableOptions: [PermissionType.none, PermissionType.edit],
              renderAs: PermissionOptionsInterface.switch,
            },
            ...(employeeMe.plan.features[ApiFeatureType.ats_career_site]?.available
              ? [
                  {
                    title: t("ats:permissions_manage_career_sites"),
                    description: t("ats:permissions_manage_career_sites_description"),
                    field: "api_permissions.manage_career_sites",
                    availableOptions: [PermissionType.none, PermissionType.edit],
                    renderAs: PermissionOptionsInterface.switch,
                  } as PermissionsItem,
                ]
              : []),
            {
              title: t("ats:job_archiving_reasons"),
              description: t("ats:permissions_manage_job_archiving_reasons_description"),
              field: "api_permissions.settings_job_archiving_reasons",
              availableOptions: [PermissionType.none, PermissionType.edit],
              renderAs: PermissionOptionsInterface.switch,
            },
          ],
        },
        {
          title: t("common:security"),
          items: [
            {
              title: t("core:roles_and_permissions"),
              description: t("core:permissions_manage_roles_and_permissions_description"),
              field: "api_permissions.settings_roles_permissions",
              availableOptions: [PermissionType.none, PermissionType.edit],
              renderAs: PermissionOptionsInterface.switch,
            },
            {
              title: t("data_protection:data_protection"),
              description: t("core:permissions_manage_data_protection_description"),
              field: "api_permissions.settings_data_protection",
              availableOptions: [PermissionType.none, PermissionType.edit],
              renderAs: PermissionOptionsInterface.switch,
            },
            {
              title: t("core:settings_security_title"),
              description: t("core:settings_security_description"),
              field: "api_permissions.settings_security",
              availableOptions: [PermissionType.none, PermissionType.edit],
              renderAs: PermissionOptionsInterface.switch,
            },
            {
              title: t("core:permissions_audit_log_read"),
              description: t("core:permissions_audit_log_read_description"),
              field: "api_permissions.audit_log_read",
              availableOptions: [PermissionType.none, PermissionType.edit],
              renderAs: PermissionOptionsInterface.switch,
            },
          ],
        },
        {
          title: t("core:settings_platform"),
          items: [
            {
              title: t("core:settings_import_data"),
              description: t("core:permissions_manage_import_data_description"),
              field: "api_permissions.data_import",
              availableOptions: [PermissionType.none, PermissionType.edit],
              renderAs: PermissionOptionsInterface.switch,
            },
            {
              title: t("core:service_users"),
              description: t("core:service_users_description"),
              field: "api_permissions.settings_api_users",
              availableOptions: [PermissionType.none, PermissionType.edit],
              renderAs: PermissionOptionsInterface.switch,
            },
            {
              title: t("core:webhooks"),
              description: t("core:webhooks_description"),
              field: "api_permissions.settings_webhooks",
              availableOptions: [PermissionType.none, PermissionType.edit],
              renderAs: PermissionOptionsInterface.switch,
            },
          ],
        },
      ],
    };

    let workflowsSection: PermissionsSection | undefined = undefined;
    const isWorkflowsAvailable = employeeMe.plan.features[ApiFeatureType.workflows].available;

    if (isWorkflowsAvailable) {
      const workflowsGroups: PermissionsSectionGroup[] = [];

      workflowsGroups.push({
        title: t("workflow:workflows"),
        items: [
          {
            title: t("workflow:workflow_module"),
            description: t("workflow:workflow_module_description"),
            field: "api_permissions.workflow_module",
            availableOptions: [PermissionType.none, PermissionType.edit],
            renderAs: PermissionOptionsInterface.switch,
            children: [
              {
                title: t("workflow:workflows_create"),
                description: t("workflow:workflows_create_description"),
                field: "api_permissions.workflows_create",
                availableOptions: [PermissionType.none, PermissionType.edit],
                renderAs: PermissionOptionsInterface.switch,
              },
              {
                title: t("workflow:workflows_executions"),
                description: t("workflow:workflows_executions_description"),
                field: "api_permissions.workflows_executions",
                availableOptions: [PermissionType.none, PermissionType.edit],
                renderAs: PermissionOptionsInterface.switch,
              },
              {
                title: t("workflow:workflows_manage_all"),
                description: t("workflow:workflows_manage_all_description"),
                field: "api_permissions.workflows_manage_all",
                availableOptions: [PermissionType.none, PermissionType.edit],
                renderAs: PermissionOptionsInterface.switch,
              },
              {
                title: t("workflow:workflows_manual_run"),
                description: t("workflow:workflows_manual_run_description"),
                field: "api_permissions.workflows_manual_run",
                availableOptions: [PermissionType.none, PermissionType.edit],
                renderAs: PermissionOptionsInterface.switch,
              },
            ],
          },
        ],
      });
      workflowsSection = {
        title: t("workflow:workflows"),
        groups: workflowsGroups,
      };
    }

    let esignSection: PermissionsSection | undefined = undefined;

    if (employeeMe.plan.features[ApiFeatureType.esign]?.available) {
      esignSection = {
        title: t("core:esign"),
        groups: [
          {
            title: t("core:esign"),
            items: [
              {
                title: t("core:permissions_esign_manage_templates_title"),
                description: t("core:permissions_esign_manage_templates_description"),
                field: "api_permissions.esign_manage_templates",
                availableOptions: [PermissionType.none, PermissionType.edit],
                renderAs: PermissionOptionsInterface.switch,
              },
              {
                title: t("core:permissions_esign_request_title"),
                description: t("core:permissions_esign_request_description"),
                field: "api_permissions.esign_request",
                availableOptions: [PermissionType.none, PermissionType.edit],
                renderAs: PermissionOptionsInterface.switch,
              },
              {
                title: t("core:permissions_esign_download_report_title"),
                description: t("core:permissions_esign_download_report_description"),
                field: "api_permissions.esign_download_report",
                availableOptions: [PermissionType.none, PermissionType.edit],
                renderAs: PermissionOptionsInterface.switch,
              },
            ],
          },
        ],
      };
    }

    let aiAssistantSection: PermissionsSection | undefined = undefined;

    if (employeeMe.plan.features[ApiFeatureType.ai_assistant]?.available) {
      aiAssistantSection = {
        title: t("ai:assistant_feature_title"),
        groups: [
          {
            title: t("ai:assistant_feature_title"),
            items: [
              {
                title: t("ai:assistant_permission_title"),
                description: t("ai:assistant_permission_description"),
                field: "api_permissions.ai_assistant_module",
                availableOptions: [PermissionType.none, PermissionType.edit],
                renderAs: PermissionOptionsInterface.switch,
              },
            ],
          },
        ],
      };
    }

    const result: PermissionsSection[] = [];
    result.push(generalSection);
    if (surveysSection) result.push(surveysSection);
    if (performanceReviewSection) result.push(performanceReviewSection);
    if (goalsSection) result.push(goalsSection);
    if (newsSection) result.push(newsSection);
    if (workflowsSection) result.push(workflowsSection);
    if (esignSection) result.push(esignSection);
    if (aiAssistantSection) result.push(aiAssistantSection);
    result.push(recruitSection);
    result.push(reportingSection);
    result.push(settingsSection);
    return result;
  };

  const permissionTypesTexts: Record<PermissionType, string> = {
    [PermissionType.none]: t("common:permission_type_none"),
    [PermissionType.view]: t("common:permission_type_view"),
    [PermissionType.edit]: t("common:permission_type_edit"),
  };

  const getAssignCardTitle = (type: RuleConditionType): string => {
    let title = `${type}`;

    RuleConditionsItems().forEach((item) => {
      if (item.id === type) title = item.title;
    });

    return `${title}`;
  };

  const getSignTitle = (sign: RuleApplySignType): string => {
    if (sign == RuleApplySignType.include) return "";
    let title = `${sign}`;

    RuleSignItems().forEach((item) => {
      if (item.id === sign) title = item.title;
    });

    return title + ": ";
  };

  const getAssignCardDescription = (rule: Rule): string => {
    if (rule.condition_type == RuleConditionType.lifecycle_status) {
      return (
        getSignTitle(rule.sign) +
        rule.condition_ids
          ?.map((entity) => LifecycleStatusTexts()[entity as ApiEmployeeLifecycleStatus] || "—")
          .join(", ")
      );
    }
    if (rule.condition_type == RuleConditionType.gender) {
      return (
        getSignTitle(rule.sign) +
        rule.condition_ids
          ?.map((entity) => {
            const gender = EmployeeGenders.find((x) => x.id === entity);
            return gender ? t(gender.title) : "—";
          })
          .join(", ")
      );
    }
    if (rule.condition_type == RuleConditionType.employment_duration) {
      return getSignTitle(rule.sign) + formatDateCondition(rule.date_condition);
    }
    if (rule.condition_type == RuleConditionType.custom_field_select) {
      const field = rule.condition_entities?.[0]?.title;
      const options = rule.value_entities?.map(({ title }) => `"${title}"`).join(", ") ?? "";

      if (field && options) {
        return (
          getSignTitle(rule.sign) +
          t("core:rule_custom_field_select_preview", {
            field: `"${field}"`,
            options,
          })
        );
      }
    }
    if (rule.condition_type == RuleConditionType.employees_with_employee_in_custom_field) {
      const fields = rule.condition_entities?.map(({ title }) => `"${title}"`).join(", ") ?? "";
      const employees = rule.value_entities?.map(({ title }) => title).join(", ") ?? "";

      if (fields && employees) {
        return (
          getSignTitle(rule.sign) +
          t("core:rule_cf_reference_preview", {
            fields,
            employees,
          })
        );
      }
    }

    const entitiesTitle = rule.condition_entities?.map((entity) => entity.title).join(", ") ?? "";
    const signTitle = getSignTitle(rule.sign);

    return entitiesTitle ? signTitle + entitiesTitle : signTitle.trim().replace(/:$/, "");
  };

  const getApplyCardTitle = (type: RuleConditionType): string => {
    let title = `${type}`;

    RuleConditionsItems().forEach((item) => {
      if (item.id === type) title = item.title;
    });

    return `${title}`;
  };

  return {
    permissionTypesTexts,
    getAssignCardTitle,
    getApplyCardTitle,
    getSignTitle,
    getAssignCardDescription,
    getEmployeeFieldPermissionSections,
    getFeaturesPermissionsSections,
  };
}
